import React, { useState, useMemo } from "react";
import styled from "styled-components";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { Toaster } from "react-hot-toast";
import { tokens, canCreatePolicy } from "../../Shared/tokens";
import { decryptData } from "../../services/crypto";
import { AddPolicyModal } from "./AddPolicyModal";

/**
 * Page header + filter bar for Policies.
 *
 * The previous version rendered two selects with a single hardcoded
 * "select" option and a Search button with no handler — controls that looked
 * live and did nothing. Filters are now lifted into PoliciesComponent and
 * applied to the grid on change, so there is no dead Search button at all.
 */
export const PoliciesFirst = ({ filters, onFiltersChange, policies = [] }) => {
  const [popUp, setPopUp] = useState(false);

  const role = useMemo(() => {
    const stored = localStorage.getItem("role");
    if (!stored) return null;
    try {
      return decryptData(stored);
    } catch (error) {
      console.error("Failed to decrypt role:", error);
      return null;
    }
  }, []);

  // Effective-year options come from the data, so the dropdown can never offer
  // a period with nothing behind it.
  const years = useMemo(() => {
    const seen = new Set();
    policies.forEach((policy) => {
      const date = new Date(policy?.effectiveFrom);
      if (!Number.isNaN(date.getTime())) seen.add(date.getFullYear());
    });
    return [...seen].sort((a, b) => b - a);
  }, [policies]);

  const update = (patch) => onFiltersChange({ ...filters, ...patch });

  const hasFilters = Boolean(filters.query || filters.year);

  return (
    <Header>
      <TitleRow>
        <div>
          <h1>Policies</h1>
          <p>Company policies and the documents that back them.</p>
        </div>
        {canCreatePolicy(role) && (
          <AddButton type="button" onClick={() => setPopUp(true)}>
            <AddIcon fontSize="small" />
            Add policy
          </AddButton>
        )}
      </TitleRow>

      <FilterRow>
        <SearchField>
          <label htmlFor="policy-search">Search</label>
          <div>
            <SearchIcon fontSize="small" aria-hidden="true" />
            <input
              type="search"
              id="policy-search"
              placeholder="Search by policy title…"
              value={filters.query}
              onChange={(e) => update({ query: e.target.value })}
            />
          </div>
        </SearchField>

        <SelectField>
          <label htmlFor="policy-year">Effective year</label>
          <select
            id="policy-year"
            value={filters.year}
            onChange={(e) => update({ year: e.target.value })}
          >
            <option value="">All years</option>
            {years.map((year) => (
              <option key={year} value={String(year)}>
                {year}
              </option>
            ))}
          </select>
        </SelectField>

        {hasFilters && (
          <ClearButton
            type="button"
            onClick={() => onFiltersChange({ query: "", year: "" })}
          >
            <CloseIcon fontSize="small" />
            Clear
          </ClearButton>
        )}
      </FilterRow>

      <AddPolicyModal open={popUp} onClose={() => setPopUp(false)} />
      <Toaster position="top-right" />
    </Header>
  );
};

const Header = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.xl};
  padding: ${tokens.space.xl};
  background-color: ${tokens.color.surface};
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.sm};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${tokens.space.lg};
  flex-wrap: wrap;

  h1 {
    margin: 0;
    font-size: 22px;
    line-height: 1.3;
    color: ${tokens.color.textPrimary};
  }

  p {
    margin: ${tokens.space.xs} 0 0;
    font-size: ${tokens.font.md};
    line-height: 1.5;
    color: ${tokens.color.textMuted};
  }
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${tokens.space.sm};
  min-height: 40px;
  padding: 0 ${tokens.space.lg};
  font-size: ${tokens.font.md};
  font-weight: 600;
  color: ${tokens.color.onPrimary};
  /* Was #0072FD, which competed with the brand teal on the Search button
     for "primary action" on the same screen. One primary now. */
  background-color: ${tokens.color.primary};
  border: 1px solid ${tokens.color.primary};
  border-radius: ${tokens.radius.md};
  cursor: pointer;
  outline: none;
  white-space: nowrap;
  transition: background-color ${tokens.motion.base} ${tokens.motion.ease};

  :hover {
    background-color: ${tokens.color.primaryHover};
    border-color: ${tokens.color.primaryHover};
  }

  :active {
    background-color: ${tokens.color.primaryActive};
  }

  :focus-visible {
    box-shadow: ${tokens.shadow.focus};
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${tokens.space.md};
  flex-wrap: wrap;
  padding-top: ${tokens.space.lg};
  border-top: 1px solid ${tokens.color.border};

  label {
    display: block;
    margin-bottom: ${tokens.space.sm};
    font-size: ${tokens.font.sm};
    font-weight: 600;
    color: ${tokens.color.textSecondary};
  }
`;

const fieldChrome = `
  min-height: 40px;
  font-size: 14px;
  color: #111827;
  background-color: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  outline: none;
  transition: border-color 180ms cubic-bezier(0.2, 0, 0.2, 1),
    box-shadow 180ms cubic-bezier(0.2, 0, 0.2, 1);

  :hover {
    border-color: #D1D5DB;
  }

  :focus-within,
  :focus-visible {
    border-color: #249393;
    box-shadow: 0 0 0 3px rgba(36, 147, 147, 0.28);
  }
`;

const SearchField = styled.div`
  flex: 1 1 260px;
  min-width: 0;

  > div {
    ${fieldChrome}
    display: flex;
    align-items: center;
    gap: ${tokens.space.sm};
    padding: 0 ${tokens.space.md};

    svg {
      color: ${tokens.color.textMuted};
      flex-shrink: 0;
    }

    input {
      flex: 1;
      min-width: 0;
      border: none;
      outline: none;
      background: transparent;
      font-size: ${tokens.font.md};
      color: ${tokens.color.textPrimary};
      padding: ${tokens.space.sm} 0;

      ::placeholder {
        color: ${tokens.color.textMuted};
      }
    }
  }
`;

const SelectField = styled.div`
  flex: 0 1 180px;

  select {
    ${fieldChrome}
    width: 100%;
    padding: 0 ${tokens.space.md};
    cursor: pointer;
  }
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.space.xs};
  min-height: 40px;
  padding: 0 ${tokens.space.md};
  font-size: ${tokens.font.md};
  font-weight: 600;
  color: ${tokens.color.textSecondary};
  background: none;
  border: 1px solid transparent;
  border-radius: ${tokens.radius.md};
  cursor: pointer;
  outline: none;
  transition: background-color ${tokens.motion.base} ${tokens.motion.ease};

  :hover {
    background-color: ${tokens.color.surfaceHover};
  }

  :focus-visible {
    box-shadow: ${tokens.shadow.focus};
  }
`;
